import type { AvatarProps } from "@replohq/lattice/components/avatar/Avatar";

import * as React from "react";

import { Avatar as LatticeAvatar } from "@replohq/lattice/components/avatar/Avatar";
import { usePortalContainer } from "@replohq/lattice/components/portal-theme/PortalTheme";
import { createPortal } from "react-dom";

const SHOULD_SHOW_DEV_USER_ID = process.env.NODE_ENV === "development";
const DEV_USER_ID_OVERLAY_OFFSET_PX = 4;
const DEBUG_OVERLAY_STORAGE_KEY = "replo.debug.overlayIsVisible";

function getDevUserIdOverlayPosition(anchor: HTMLElement) {
  if (!document.body.contains(anchor)) {
    return null;
  }

  const rect = anchor.getBoundingClientRect();
  const isVisible =
    rect.width > 0 &&
    rect.height > 0 &&
    rect.bottom >= 0 &&
    rect.right >= 0 &&
    rect.top <= window.innerHeight &&
    rect.left <= window.innerWidth;

  if (!isVisible) {
    return null;
  }

  return {
    top: rect.bottom + DEV_USER_ID_OVERLAY_OFFSET_PX,
    left: rect.left,
  };
}

function getDebugOverlayIsVisible() {
  if (typeof window === "undefined") {
    return false;
  }

  return window.localStorage.getItem(DEBUG_OVERLAY_STORAGE_KEY) === "true";
}

function subscribeToDebugOverlayVisibility(callback: () => void) {
  if (typeof window === "undefined") {
    return () => {};
  }

  window.addEventListener("replostorage", callback);
  window.addEventListener("storage", callback);

  return () => {
    window.removeEventListener("replostorage", callback);
    window.removeEventListener("storage", callback);
  };
}

function useShouldShowDevUserIdOverlay() {
  const debugOverlayIsVisible = React.useSyncExternalStore(
    subscribeToDebugOverlayVisibility,
    getDebugOverlayIsVisible,
    () => false,
  );

  return SHOULD_SHOW_DEV_USER_ID && debugOverlayIsVisible;
}

function DevUserIdOverlay({
  anchorRef,
  userId,
}: {
  anchorRef: React.RefObject<HTMLSpanElement>;
  userId: string;
}) {
  const [position, setPosition] = React.useState<{
    top: number;
    left: number;
  } | null>(null);
  const [copied, setCopied] = React.useState(false);
  const copiedTimeoutRef = React.useRef<number | null>(null);
  const portalContainer = usePortalContainer();

  // eslint-disable-next-line replo/no-use-effect -- Keeps the portalled debug overlay anchored to a DOM node owned outside this component.
  React.useEffect(() => {
    const updatePosition = () => {
      const anchor = anchorRef.current;
      if (!anchor) {
        return;
      }

      setPosition(getDevUserIdOverlayPosition(anchor));
    };

    const updatePositionAfterInteraction = () => {
      window.setTimeout(updatePosition, 0);
    };

    updatePosition();
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);
    window.addEventListener(
      "pointerdown",
      updatePositionAfterInteraction,
      true,
    );
    window.addEventListener("keydown", updatePositionAfterInteraction, true);

    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
      window.removeEventListener(
        "pointerdown",
        updatePositionAfterInteraction,
        true,
      );
      window.removeEventListener(
        "keydown",
        updatePositionAfterInteraction,
        true,
      );
    };
  }, [anchorRef]);

  const copyUserId = React.useCallback(() => {
    if (!navigator.clipboard) {
      return;
    }

    void navigator.clipboard.writeText(userId).then(() => {
      setCopied(true);
      if (copiedTimeoutRef.current !== null) {
        window.clearTimeout(copiedTimeoutRef.current);
      }
      copiedTimeoutRef.current = window.setTimeout(() => {
        setCopied(false);
        copiedTimeoutRef.current = null;
      }, 1000);
    });
  }, [userId]);

  const stopOverlayEvent = React.useCallback(
    (event: React.SyntheticEvent<HTMLButtonElement>) => {
      event.stopPropagation();
    },
    [],
  );

  const handleClick = React.useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      event.stopPropagation();
      copyUserId();
    },
    [copyUserId],
  );

  if (!position || typeof document === "undefined") {
    return null;
  }

  return createPortal(
    <button
      aria-label="Copy user id"
      className="fixed z-50 max-w-[min(90vw,20rem)] cursor-pointer truncate rounded-md border-2 border-amber bg-amber-soft px-1 py-0.5 font-mono text-[10px] font-medium leading-none text-black shadow-lg opacity-90 hover:bg-amber"
      onClick={handleClick}
      onMouseDown={stopOverlayEvent}
      onPointerDown={stopOverlayEvent}
      style={{
        top: position.top,
        left: position.left,
      }}
      title="Copy user id"
      type="button"
    >
      {copied ? "copied" : `user ${userId}`}
    </button>,
    portalContainer ?? document.body,
  );
}

export function Avatar<ClassName extends string>(
  props: AvatarProps<ClassName>,
) {
  const anchorRef = React.useRef<HTMLSpanElement>(null);
  const shouldShowDevUserIdOverlay = useShouldShowDevUserIdOverlay();

  return (
    <span ref={anchorRef} className="inline-flex align-middle">
      <LatticeAvatar {...props} />
      {props.user && shouldShowDevUserIdOverlay && (
        <DevUserIdOverlay anchorRef={anchorRef} userId={props.user.id} />
      )}
    </span>
  );
}
